/**
 * AiThinkingOrb - „AI is thinking" animation (Gemini-style sparkle). Shared in @wa/ui
 * so that both the public „Alusta" (Start) funnel (web_account) and the admin „Alusta"
 * preview (web_admin) use an identical orb.
 *
 * A rotating + pulsing four-pointed sparkle star with a gradient fill, a smaller companion
 * sparkle twinkles beside it, a breathing aurora glow behind. All of it is pure CSS (GPU compositing),
 * theme colours via `--color-primary(-2)` → follows the brand + dark mode.
 * `prefers-reduced-motion` stops the motion and leaves a nice static frame.
 * Decorative → `aria-hidden`.
 */
export function AiThinkingOrb({ className = "" }: { className?: string }) {
  return (
    <div className={`agrav-star ${className}`} aria-hidden="true">
      <style>{ORB_CSS}</style>

      {/* Breathing aurora glow */}
      <span className="agrav-glow" />

      {/* Large rotating + pulsing sparkle */}
      <span className="agrav-wrap agrav-wa">
        <span className="agrav-spark agrav-big" />
      </span>

      {/* Small twinkling companion sparkle */}
      <span className="agrav-wrap agrav-wb">
        <span className="agrav-spark agrav-sm" />
      </span>
    </div>
  );
}

const ORB_CSS = `
.agrav-star {
  position: relative;
  width: 7rem;            /* 112px */
  height: 7rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  isolation: isolate;
}

/* Aurora glow in the background */
.agrav-glow {
  position: absolute;
  left: 50%; top: 50%;
  width: 5.5rem; height: 5.5rem;
  margin: -2.75rem 0 0 -2.75rem;
  border-radius: 9999px;
  background: radial-gradient(
    circle,
    color-mix(in oklab, var(--color-primary) 42%, transparent),
    color-mix(in oklab, var(--color-primary-2, var(--color-primary)) 24%, transparent) 55%,
    transparent 70%
  );
  filter: blur(9px);
  animation: agrav-glow 3s ease-in-out infinite;
  z-index: 0;
}

/* Sparkle center points (the rotation happens inside, so the centering is not lost).
   The glow sits here, not on the sparkle: a filter runs before clip-path on the same
   element, so a drop-shadow on the clipped sparkle itself is cut away with the rest. */
.agrav-wrap {
  position: absolute; left: 50%; top: 50%;
  filter: drop-shadow(0 0 7px color-mix(in oklab, var(--color-primary) 70%, transparent));
}
.agrav-wa { width: 4rem;   height: 4rem;   margin: -2rem 0 0 -2rem; z-index: 1; }
.agrav-wb { width: 1.7rem; height: 1.7rem; margin: -2.15rem 0 0 0.25rem; z-index: 2; }

/* display:block because the sparkle is an empty <span>: inline, it ignores the
   100% size and measures 0x0, so nothing paints. */
.agrav-spark {
  display: block;
  width: 100%; height: 100%;
  background: linear-gradient(135deg, var(--color-primary), var(--color-primary-2, var(--color-primary)));
  clip-path: polygon(50% 0, 61% 39%, 100% 50%, 61% 61%, 50% 100%, 39% 61%, 0 50%, 39% 39%);
}
.agrav-big { animation: agrav-rot 6s linear infinite, agrav-pulse 2.4s ease-in-out infinite; }
.agrav-sm  { animation: agrav-twk 1.8s ease-in-out infinite; }

@keyframes agrav-rot   { to { transform: rotate(360deg); } }
@keyframes agrav-pulse { 0%, 100% { opacity: 0.92; } 50% { opacity: 1; } }
@keyframes agrav-twk   { 0%, 100% { opacity: 0.3; }  50% { opacity: 1; } }
@keyframes agrav-glow  { 0%, 100% { opacity: 0.5; }  50% { opacity: 1; } }

@media (prefers-reduced-motion: reduce) {
  .agrav-glow, .agrav-big, .agrav-sm { animation: none !important; }
}
`;
